import { Wallet } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
    useCustodialPanelState,
    useSnappyWalletState,
} from '@/features/custodial';
import { useCustodialJobs } from '@/features/custodial/hooks/useCustodialJobs';
import { isActiveSpeedJob } from '@/features/custodial/jobStatus';
import { useOnboardingLauncher } from '@/features/onboarding/store';

/**
 * The single onboarding prompt on the trading view, for the two states where
 * the user has no Snappy Wallet yet (S2a/S2b).
 *
 * It sits in the slot ReactivateAgentBanner occupies because those states are
 * mutually exclusive: the reactivation banner is for users who already HAVE a
 * wallet. Consolidating the old "setup required" / "do the setup" / "deposit to
 * start" trio into one prompt removed all three from this view, which left a
 * signed-in wallet-less user with no visible way forward — the CustodialPanel
 * copy only appears once the deposit panel is already open. This is that way
 * forward, and it is the only prompt shown.
 *
 * Each cohort gets its own instruction. A wallet-first user has no social login
 * and does not need one: their custodial wallets already exist on chain, and
 * the first deposit activates them. Telling them to "sign in with Google" or to
 * "finish setting up" (the old copy) sent them back to the start of a flow they
 * had already completed.
 */
const SnappyOnboardingBanner: React.FC = () => {
    const snappy = useSnappyWalletState();
    const launchOnboarding = useOnboardingLauncher((s) => s.launch);
    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const { jobs } = useCustodialJobs();
    // the deposit this banner asks for is already under way: telling the user they are one
    // deposit away while their deposit bridges is noise
    const depositInProgress = jobs.some(
        (job) => job.kind === 'deposit' && isActiveSpeedJob(job),
    );

    if (
        (snappy.status !== 'signInRequired' &&
            snappy.status !== 'firstDepositRequired') ||
        depositInProgress
    ) {
        return null;
    }

    // named so the user knows the deposit IS the setup — there is no separate step to find
    const copy =
        snappy.status === 'signInRequired'
            ? {
                title: 'Finish setting up your Snappy Wallet',
                detail: 'Connect your Cronos wallet to create your gasless wallet. You can add a social login afterwards.',
                action: 'Continue setup',
            }
            : snappy.cohort === 'wallet'
                ? {
                    title: 'One deposit away',
                    detail: 'Your Snappy Wallet is ready. Make your first deposit to activate trading.',
                    action: 'Deposit to activate',
                }
                : {
                    title: 'One deposit away',
                    detail: `${snappy.displayName ? `Signed in as ${snappy.displayName}. ` : ''}Your Snappy Wallet is created automatically with your first deposit.`,
                    action: 'Deposit to activate',
                };

    const onAction = () => {
        if (snappy.status === 'firstDepositRequired') {
            openDeposit();
            return;
        }
        launchOnboarding('wallet');
    };

    return (
        <div
            role="status"
            className="mb-4 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
        >
            <div className="flex items-start gap-2">
                <Wallet size={14} className="mt-0.5 shrink-0 text-primary" />
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-primary">
                        {copy.title}
                    </p>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                        {copy.detail}
                    </p>
                </div>
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={onAction}
                className="mt-2.5 w-full gap-1 border-primary/30 text-primary hover:border-primary/50 hover:bg-primary/10"
            >
                <Wallet size={14} />
                {copy.action}
            </Button>
        </div>
    );
};

SnappyOnboardingBanner.displayName = 'SnappyOnboardingBanner';

export default SnappyOnboardingBanner;
